import { useCallback, useMemo } from "react";
import type { BoardCollapsedChange } from "@design-system/Board/Board.types";
import {
    applyColumnCollapseToggle,
    type BoardColumnFill,
    haveSameIds,
    resolveCollapsedColumnIds,
} from "./boardColumnCollapse";
import type { BoardCollapsed } from "./boardLayoutCache";
import { useSessionExpandedColumns } from "./useSessionExpandedColumns";

/** The view key while no view is picked. */
export const NO_VIEW_KEY = "none";

/**
 * Writes the stored collapse `next` makes from the latest stored one, where
 * `collapsedColumnIds` are the columns collapsed by hand. `next` returns
 * null when nothing stored changes, so the write can be skipped.
 */
export type UpdateStoredBoardCollapse = (
    next: (latest: BoardCollapsed) => BoardCollapsed | null,
) => void;

interface UseBoardColumnCollapseArgs {
    viewKey: string;
    columns: readonly BoardColumnFill[];
    /** The stored columns collapsed by hand while they had cards. */
    handCollapsedIds: readonly string[];
    updateStored: UpdateStoredBoardCollapse;
}

/**
 * A board's collapsed columns: empty ones and hand-collapsed ones, minus
 * those expanded this session. Toggling an empty column changes only the
 * session, per view key; toggling a column with cards, or a lane, is stored.
 */
export const useBoardColumnCollapse = ({
    viewKey,
    columns,
    handCollapsedIds,
    updateStored,
}: UseBoardColumnCollapseArgs) => {
    const { getSessionExpandedIds, setSessionExpandedIds } =
        useSessionExpandedColumns();
    const sessionExpandedIds = getSessionExpandedIds(viewKey);

    const collapsedColumnIds = useMemo(
        () =>
            resolveCollapsedColumnIds({
                columns,
                handCollapsedIds,
                sessionExpandedIds,
            }),
        [columns, handCollapsedIds, sessionExpandedIds],
    );

    const onCollapsedChange = useCallback(
        (change: BoardCollapsedChange) => {
            const toggle = {
                columns,
                sessionExpandedIds,
                collapsedColumnIds: change.collapsedColumnIds,
            };
            setSessionExpandedIds(
                viewKey,
                applyColumnCollapseToggle({ ...toggle, handCollapsedIds })
                    .sessionExpandedIds,
            );
            updateStored((latest) => {
                const nextHandCollapsedIds = applyColumnCollapseToggle({
                    ...toggle,
                    handCollapsedIds: latest.collapsedColumnIds,
                }).handCollapsedIds;
                const isUnchanged =
                    nextHandCollapsedIds === latest.collapsedColumnIds &&
                    haveSameIds(
                        latest.collapsedLaneIds,
                        change.collapsedLaneIds,
                    );
                return isUnchanged
                    ? null
                    : {
                          collapsedColumnIds: [...nextHandCollapsedIds],
                          collapsedLaneIds: [...change.collapsedLaneIds],
                      };
            });
        },
        [
            columns,
            sessionExpandedIds,
            viewKey,
            handCollapsedIds,
            setSessionExpandedIds,
            updateStored,
        ],
    );

    return { collapsedColumnIds, onCollapsedChange };
};
